import { useReactiveVar } from "@apollo/client";
import {
  Button,
  Divider,
  Form,
  Input,
  Checkbox,
  Upload,
  message,
  Modal,
} from "antd";
import type { UploadProps } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "./OneOnOneDetail.scss";
import { OneOnOneDetailType } from "./types";
import ImagePreviewModal from "./utils/ImagePreviewModal";
import FileDownload from "./utils/FileDownload";

import { header } from "../utils/salesAdminHeader";
import { convertAllFilesToApiFormat } from "./utils/convertToBase64";
import TiptapEditor from "./utils/TiptapEditor";

import ContentTitle from "../../../components/ui/ContentTitle";
import { __currentUser__ } from "../../../graphql/policies";
import { apiCall } from "../../../utils/apiCall";
import GeneralModal from "../../../utils/GeneralModal";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

//Permission usage
import { Dispatch, SetStateAction } from "react";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";

interface OneOnOneBoardDetailProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const OneOnOneDetail = ({
  setSelectedService,
  setSelectSubItem,
}: OneOnOneBoardDetailProps) => {
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState<OneOnOneDetailType[]>([]);
  const [comments, setComments] = useState<OneOnOneDetailType[]>([]);
  const [isResolved, setIsResolved] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [modalOpen, setModalOpen] = useState("");
  const [checked, setChecked] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const currentUser = useReactiveVar(__currentUser__);

  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const stripHTML = (html: string): string => {
    return html.replace(/<[^>]+>/g, "").replace(/\s+/g, "");
  };

  const imageProps: UploadProps = {
    name: "file",
    action: "https://httpbin.org/post",
    headers: {
      authorization: "authorization-text",
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image");
      const isLt1MB = file.size < 1024 * 1024;

      if (!isImage) {
        message.error(renderAntDMessageConfig("Wrong file format!"));
        return Upload.LIST_IGNORE;
      }

      if (!isLt1MB) {
        message.error(
          renderAntDMessageConfig("이미지 크기는 1MB 미만이어야 합니다!")
        );
        return Upload.LIST_IGNORE;
      }
    },
  };

  const fileProps: UploadProps = {
    name: "file",
    action: "https://httpbin.org/post",
    headers: {
      authorization: "authorization-text",
    },
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image");
      const isLt1MB = file.size < 1024 * 1024;

      if (isImage) {
        message.error(
          renderAntDMessageConfig("이미지는 이미지 업로드 영역에 첨부해주세요!")
        );
        return Upload.LIST_IGNORE;
      }

      if (!isLt1MB) {
        message.error(
          renderAntDMessageConfig("파일 크기는 1MB 미만이어야 합니다!")
        );
        return Upload.LIST_IGNORE;
      }
    },
  };

  const fetchDetails = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + `/contents/admin/messages/${id}`,
    });
    if (data) {
      setDetails(data);
    }
  };

  const fetchResolvedStatus = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN +
        `/contents/admin/messages/${
          details[details.length - 1].replyId
        }/resolved`,
    });
    if (data) {
      setIsResolved(data.isResolved);
      setDisabled(data.disabled);
    }
  };

  const handleSubmit = async () => {
    const currentForm = isEdit ? editForm : form;
    try {
      const values = await currentForm.validateFields();

      const imageFiles = currentForm.getFieldValue("images") || [];
      const attachmentFiles = currentForm.getFieldValue("files") || [];

      const { images, files } = await convertAllFilesToApiFormat(
        imageFiles,
        attachmentFiles
      );

      const payload = {
        body: values.content,
        operator: currentUser?.userName,
        ...(images.length > 0 && { images }),
        ...(files.length > 0 && { files }),
        replyId: details[details.length - 1].replyId,
      };
      await apiCall({
        method: isEdit ? "PATCH" : "POST",
        header: header,
        payload: payload,
        endPoint:
          process.env.REACT_APP_SALES_ADMIN +
          "/contents/admin/messages" +
          (isEdit ? `/${details[details.length - 1].contentId}` : ""),
      });
      navigate("/sales188/one-on-one-board");
    } catch (err) {
      console.log("error", err);
    }
  };

  const handleDelete = async () => {
    try {
      await apiCall({
        method: "PATCH",
        header: header,
        payload: {
          operator: currentUser?.userName,
          replyId: details[details.length - 1].replyId,
        },
        endPoint:
          process.env.REACT_APP_SALES_ADMIN +
          `/contents/admin/messages/${
            details[details.length - 1].contentId
          }/deleted`,
      });
      window.location.reload();
    } catch (err) {
      console.log("error", err);
    }
  };

  const handleCheckEvent = (e: boolean) => {
    if (e) {
      setChecked(true);
    } else {
      setChecked(false);
    }
    setModalOpen("2");
  };

  const handleSwitchResolvedStatus = async (e: boolean) => {
    await apiCall({
      method: e ? "POST" : "PATCH",
      header: header,
      payload: { operator: currentUser?.userName },
      endPoint:
        process.env.REACT_APP_SALES_ADMIN +
        `/contents/admin/messages/${
          details[details.length - 1].replyId
        }/resolve` +
        (e ? "" : "/deleted"),
    });
    fetchResolvedStatus();
    setModalOpen("");
  };

  useEffect(() => {
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("업무 관리");
    fetchDetails();
  }, []);

  useEffect(() => {
    if (details.length > 0) {
      fetchResolvedStatus();
    }
    if (details.length > 1) {
      const replica = details.slice();
      replica.shift();
      setComments(replica);
      const imageFileList = details[details.length - 1].blobImageUrls?.map(
        (url, idx) => ({
          uid: `image-${idx}`,
          name: `image-${idx}.jpg`,
          status: "done",
          url: url,
        })
      );

      const fileFileList = details[details.length - 1].blobFiles?.map(
        (file, idx) => ({
          uid: `file-${idx}`,
          name: file.fileName,
          status: "done",
          url: file.fileUrl,
        })
      );

      editForm.setFieldsValue({
        images: imageFileList,
        files: fileFileList,
        content: details[details.length - 1].body,
      });
    }
  }, [details]);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Business Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="oneOnOneDetailWrapper">
      <div className="sticky top-0 z-999">
        <ContentTitle
          title="1:1 게시판"
          returnTitle="1:1 게시판"
          onReturn={() => {
            navigate(-1);
          }}
        />
      </div>
      <div className="content-container">
        {!disabled && (
          <div className="flex justify-end">
            <Checkbox
              checked={isResolved}
              onChange={(e) => {
                handleCheckEvent(e.target.checked);
              }}
            >
              답변 완료 처리하기
            </Checkbox>
          </div>
        )}
        <div className="mt-5 info-bar flex justify-between items-center text-center">
          <div className="flex items-center text-center w-3/4">
            <p className="pl-5 w-1/12">{id}</p>
            <p className="pl-5 w-2/5">{details[0]?.category}</p>
            <p className="pl-5 w-2/3 font-bold text-xl">{details[0]?.title}</p>
          </div>
          <div className="flex justify-end">
            <p className="pr-5">
              {details[0]?.creator}({details[0]?.companyNumber})
            </p>
            <p className="pr-5">
              {details[0]?.date
                ? details[0].date.split(".")[0].replace("T", " ")
                : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div
            className="w-3/4 mt-5 body-area"
            dangerouslySetInnerHTML={{ __html: details[0]?.body || "" }}
          />
          <div className="flex flex-col items-center w-2/3 mt-5">
            {details[0]?.blobImageUrls?.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`announcement-${index}`}
                className="my-2 attached-image"
                onClick={() => setPreviewImageUrl(url)}
                style={{ cursor: "pointer" }}
              />
            ))}
            {details[0] &&
              (details[0]?.blobFiles.length > 0 ||
                details[0].attachments.length > 0) && (
                <div className="divider">
                  <Divider orientation="left">Attached File</Divider>
                </div>
              )}
            {details[0]?.blobFiles?.map((file, index) => (
              <FileDownload
                key={`blob-${index}`}
                fileName={file.fileName}
                fileUrl={file.fileUrl}
                index={index}
              />
            ))}
            {details[0]?.attachments?.map((file, index) => (
              <FileDownload
                key={`attachment-${index}`}
                fileName={file.fileName}
                base64={file.data}
                contentType={file.contentType}
                fileId={file.fileId}
                index={index}
              />
            ))}
          </div>
        </div>
        <div className="w-full mt-4">
          <Divider orientation="left">답글 {comments.length}</Divider>
        </div>
        <div className="content-container">
          {comments.map((item, index) => {
            return (
              <div className={`${index === 0 ? "" : "mt-16"}`}>
                <div className="flex justify-start items-center">
                  <p className="font-bold text-base">{item.creator}</p>
                  <p className="text-sm reminder-text ml-3">
                    {item.date.split(".")[0].replace("T", " ")}
                  </p>
                </div>
                <div className="mt-10 flex gap-4">
                  {item.blobImageUrls.map((images, index) => {
                    return (
                      <img
                        className="comment-image"
                        src={images}
                        alt={`img-${index - 1}`}
                        onClick={() => setPreviewImageUrl(images)}
                        style={{ cursor: "pointer" }}
                      />
                    );
                  })}
                </div>
                <div
                  className="mt-4 body-area"
                  dangerouslySetInnerHTML={{ __html: item.body }}
                />
                <div className="mt-10">
                  {item.blobFiles?.map((file, index) => (
                    <FileDownload
                      key={`blob-${index}`}
                      fileName={file.fileName}
                      fileUrl={file.fileUrl}
                      index={index}
                    />
                  ))}
                  {item.attachments?.map((file, index) => (
                    <FileDownload
                      key={`attachment-${index}`}
                      fileName={file.fileName}
                      base64={file.data}
                      contentType={file.contentType}
                      fileId={file.fileId}
                      index={index}
                    />
                  ))}
                </div>
                {index === comments.length - 1 &&
                  item.creatorEnglishName.toLowerCase() === currentUser?.userName?.toLowerCase() &&
                  !isResolved &&
                  !isEdit && (
                    <div className="flex justify-start mt-6">
                      <Button
                        type="link"
                        onClick={() => {
                          setIsEdit(true);
                        }}
                      >
                        수정
                      </Button>
                      <Button
                        className="ml-3"
                        type="link"
                        onClick={() => {
                          setModalOpen("1");
                        }}
                      >
                        삭제
                      </Button>
                    </div>
                  )}
              </div>
            );
          })}
          {isEdit && (
            <Form form={editForm}>
              <Form.Item required wrapperCol={{ span: 20 }} className="mt-10">
                <TiptapEditor
                  value={editForm.getFieldValue("content")}
                  onChange={(html) =>
                    editForm.setFieldsValue({ content: html })
                  }
                />
                <Form.Item
                  name="content"
                  rules={[
                    {
                      required: true,
                      validator: (_, value) => {
                        const hasMeaningfulContent =
                          !!value &&
                          /<[^>]+>(.*?)<\/[^>]+>/.test(value) &&
                          stripHTML(value).trim().length > 0;

                        return hasMeaningfulContent
                          ? Promise.resolve()
                          : Promise.reject(new Error("내용을 입력해주세요."));
                      },
                    },
                  ]}
                  valuePropName="value"
                  getValueFromEvent={(v) => v}
                  noStyle
                >
                  <Input hidden />
                </Form.Item>
              </Form.Item>
              <Form.Item
                className="mt-10 image-upload"
                name="images"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e?.fileList) ? e.fileList : []
                }
              >
                <Upload listType="picture" {...imageProps}>
                  <Button icon={<UploadOutlined />}>이미지 업로드</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                className="image-upload"
                name="files"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e?.fileList) ? e.fileList : []
                }
              >
                <Upload {...fileProps}>
                  <Button icon={<UploadOutlined />}>파일 업로드</Button>
                </Upload>
              </Form.Item>
            </Form>
          )}
          {isEdit && (
            <div className="flex justify-end mt-4">
              <Button
                type="link"
                onClick={() => {
                  setIsEdit(false);
                }}
              >
                취소
              </Button>
              <Button
                className="ml-4 submit-btn"
                type="primary"
                onClick={handleSubmit}
              >
                저장
              </Button>
            </div>
          )}
          {!isResolved && !isEdit && (
            <p className="font-bold text-base mt-16">답글 추가하기</p>
          )}
          {!isResolved && !isEdit && (
            <Form form={form}>
              <Form.Item required wrapperCol={{ span: 20 }} className="mt-10">
                <div className="tiptap-wrapper">
                  <TiptapEditor
                    value={form.getFieldValue("content")}
                    onChange={(html) => form.setFieldsValue({ content: html })}
                  />
                  <Form.Item
                    name="content"
                    rules={[
                      {
                        required: true,
                        validator: (_, value) => {
                          const hasMeaningfulContent =
                            !!value &&
                            /<[^>]+>(.*?)<\/[^>]+>/.test(value) &&
                            stripHTML(value).trim().length > 0;

                          return hasMeaningfulContent
                            ? Promise.resolve()
                            : Promise.reject(new Error("내용을 입력해주세요."));
                        },
                      },
                    ]}
                    valuePropName="value"
                    getValueFromEvent={(v) => v}
                    noStyle
                  >
                    <Input hidden />
                  </Form.Item>
                </div>
              </Form.Item>
              <Form.Item
                className="mt-10 image-upload"
                name="images"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e?.fileList) ? e.fileList : []
                }
              >
                <Upload listType="picture" {...imageProps}>
                  <Button icon={<UploadOutlined />}>이미지 업로드</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                className="image-upload"
                name="files"
                valuePropName="fileList"
                getValueFromEvent={(e) =>
                  Array.isArray(e?.fileList) ? e.fileList : []
                }
              >
                <Upload {...fileProps}>
                  <Button icon={<UploadOutlined />}>파일 업로드</Button>
                </Upload>
              </Form.Item>
            </Form>
          )}
          {!isResolved && !isEdit && (
            <div className="flex justify-end mt-4">
              <Button
                type="link"
                onClick={() => {
                  navigate(-1);
                }}
              >
                취소
              </Button>
              <Button
                className="ml-4 submit-btn"
                type="primary"
                onClick={handleSubmit}
              >
                저장
              </Button>
            </div>
          )}
        </div>
      </div>
      <GeneralModal
        open={modalOpen === "1"}
        isDeletion
        isConfirm={false}
        handleDelete={handleDelete}
        close={() => {
          setModalOpen("");
        }}
        width={400}
        title="이 코멘트를 삭제할까요?"
        text="삭제된 코멘트는 복구할 수 없습니다."
        noBtnText="취소"
        confirmBtnText="확인"
      />
      <GeneralModal
        open={modalOpen === "2"}
        close={() => {
          setModalOpen("");
        }}
        isDeletion={false}
        isConfirm
        handleConfirm={() => {
          handleSwitchResolvedStatus(checked);
        }}
        width={400}
        title={
          checked
            ? "이 게시글을 답변완료 처리 할까요?"
            : "이 게시글을 답변 대기로 변경할까요?"
        }
        text={
          checked
            ? "답변 완료 처리 후 코멘트를 달 수 없습니다."
            : "답변 완료 처리가 취소됩니다."
        }
        noBtnText="취소"
        confirmBtnText="확인"
      />
      <ImagePreviewModal
        url={previewImageUrl}
        close={() => setPreviewImageUrl(null)}
      />
    </div>
  );
};

export default OneOnOneDetail;
